import { heatRows, heatStep, localMidnight } from "../glance";
import { PointIcon } from "../icon";
import { boundPoint, Frame, key, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

/** One sequential ramp, light to dark, five steps (WLB-04). */
const RAMP = ["bg-blue-100", "bg-blue-200", "bg-blue-400", "bg-blue-600", "bg-blue-800"] as const;
const DAYS = { "7d": 7, "14d": 14, "28d": 28 } as const;

/** The first local midnight of a heatmap's range (WLB-14): 6, 13 or 27 days before today's. */
export function heatmapFrom(range: unknown, now: Date, tz?: string): number {
  const days = DAYS[range as keyof typeof DAYS] ?? 7;
  return localMidnight(now.getTime(), tz, days - 1);
}

/**
 * heatmap: days (rows, oldest at the top) by hour of day (columns), each hour in a step of one ramp from the range's
 * lowest to its highest value, with a Less … More legend (VisionPlatform widget-library-2 WLB-14).
 */
export function Heatmap({ widget, input, now, tz }: WidgetProps) {
  const fmt = useWidgetText();
  const bound = boundPoint(widget, input);
  const point = bound?.point;
  const name = widget.title ?? point?.name ?? "";
  const history = bound?.binding.datapoint
    ? input.history?.[key(bound.binding.device, bound.binding.datapoint)]
    : undefined;
  const rows = heatRows(history, heatmapFrom(widget.config?.range, now, tz), now.getTime(), tz);
  const values = rows.flatMap((r) => r.cells.filter((v): v is number => v !== null));
  if (!values.length) {
    return (
      <Frame widget={widget} state="empty" title={name} icon={<PointIcon point={point} />} label={name}>
        <p className="text-gray-700">{fmt.t("state.noData")}</p>
      </Frame>
    );
  }
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const unit = { unit: point?.unit ?? null, decimals: point?.decimals ?? 0 };
  const hour = (h: number) => `${String(h).padStart(2, "0")}:00`;
  return (
    <Frame widget={widget} state="normal" title={name} icon={<PointIcon point={point} />} label={name}>
      <div className="grid flex-1 grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs text-gray-700">
        {rows.map((r) => (
          <div key={r.day} className="contents">
            <span className="leading-4">{fmt.t(`weekday.${r.weekday}`)}</span>
            <div className="grid grid-cols-24 gap-0.5">
              {r.cells.map((v, h) => (
                <span
                  key={h}
                  title={v === null ? undefined : `${fmt.t(`weekday.${r.weekday}`)} ${hour(h)}: ${fmt.value(v, unit)}`}
                  className={`h-4 rounded-sm ${v === null ? "" : RAMP[heatStep(v, lo, hi)]}`}
                />
              ))}
            </div>
          </div>
        ))}
        <span />
        <div aria-hidden="true" className="flex justify-between tabular-nums">
          {["00", "06", "12", "18", "24"].map((h) => (
            <span key={h}>{h}</span>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-end gap-1 text-xs text-gray-700">
        <span>{fmt.t("heatmap.less")}</span>
        {RAMP.map((c) => (
          <span key={c} aria-hidden="true" className={`size-2.5 rounded-sm ${c}`} />
        ))}
        <span>{fmt.t("heatmap.more")}</span>
      </div>
      <ul className="sr-only">
        {rows.flatMap((r) =>
          r.cells.flatMap((v, h) =>
            v === null ? [] : [<li key={`${r.day}-${h}`}>{`${r.day} ${hour(h)}: ${fmt.value(v, unit)}`}</li>],
          ),
        )}
      </ul>
    </Frame>
  );
}
